import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const useSyncPartnerProfile = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is a partner and needs profile sync
  const { data: syncNeeded, isLoading } = useQuery({
    queryKey: ['partner-sync-check', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('🔄 [PartnerSync] No session user');
        return null;
      }

      console.log('🔄 [PartnerSync] Starting sync check for user:', session.user.email);

      // Get user's profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, phone, contact_type, external_owner_id')
        .eq('user_id', session.user.id)
        .single();

      if (profileError) {
        console.error('❌ [PartnerSync] Profile fetch error:', profileError);
      }

      console.log('🔄 [PartnerSync] Current profile:', profile);

      // Get partner record if exists
      const { data: partner, error: partnerError } = await supabase
        .from('crm_partners')
        .select('id, first_name, last_name, email, phone')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (partnerError) {
        console.error('❌ [PartnerSync] Partner fetch error:', partnerError);
      }

      console.log('🔄 [PartnerSync] Partner record:', partner);

      if (!partner || !profile) {
        console.log('🔄 [PartnerSync] Missing partner or profile data');
        return null;
      }

      // Check if sync is needed
      const needsSync = 
        !profile.first_name || 
        !profile.last_name || 
        !profile.email || 
        profile.contact_type !== 'Partner' ||
        !profile.external_owner_id;

      console.log('🔄 [PartnerSync] Sync needed?', needsSync, {
        hasFirstName: !!profile.first_name,
        hasLastName: !!profile.last_name,
        hasEmail: !!profile.email,
        isPartnerType: profile.contact_type === 'Partner',
        hasExternalOwner: !!profile.external_owner_id
      });

      return needsSync ? { profile, partner } : null;
    },
    enabled: !!session?.user?.id,
    refetchOnWindowFocus: false,
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async ({ profile, partner }: { profile: any; partner: any }) => {
      const updates: any = {};

      // Sync basic info from partner to profile if missing
      if (!profile.first_name && partner.first_name) {
        updates.first_name = partner.first_name;
      }
      if (!profile.last_name && partner.last_name) {
        updates.last_name = partner.last_name;
      }
      if (!profile.email && partner.email) {
        updates.email = partner.email;
      }
      if (!profile.phone && partner.phone) {
        updates.phone = partner.phone;
      }

      // Always ensure partner tagging
      updates.contact_type = 'Partner';
      updates.external_owner_id = partner.id;

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', session!.user.id);

      if (error) throw error;
      
      console.log('✅ [PartnerSync] Partner profile synced successfully:', updates);
      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-sync-check'] });
      queryClient.invalidateQueries({ queryKey: ['partner-profile'] });
      console.log('✅ [PartnerSync] Queries invalidated after successful sync');
    },
  });

  // Auto-sync when needed
  useEffect(() => {
    if (syncNeeded && !syncMutation.isPending) {
      console.log('🔄 [PartnerSync] Auto-syncing partner profile...', syncNeeded);
      syncMutation.mutate(syncNeeded);
    }
  }, [syncNeeded, syncMutation.isPending]);

  return {
    syncNeeded: !!syncNeeded,
    isLoading,
    isSyncing: syncMutation.isPending,
    syncError: syncMutation.error,
    manualSync: () => {
      if (syncNeeded) {
        syncMutation.mutate(syncNeeded);
      }
    }
  };
};